/**
 * Undo/Redo middleware - intercepts undoable actions and builds history
 */

import { addToHistory, updateLastInverseAction, replaceLastWithTransaction } from '../slices/undoRedoSlice';
import {
  setHasUnsavedChanges,
  selectLastSavedHistoryLength,
} from '../slices/editorUISlice';

function syncDirtyState(store) {
  const state = store.getState();
  const pastLength = state.undoRedo.past.length;
  const savedLength = selectLastSavedHistoryLength(state) || 0;
  const nextDirty = pastLength !== savedLength;

  if (state.editorUI.hasUnsavedChanges !== nextDirty) {
    store.dispatch(setHasUnsavedChanges(nextDirty));
  }
}

/**
 * Creates inverse action for undoable actions
 */
function createInverseAction(action, state) {
  const { type, payload } = action;

  // Timeline actions
  if (type === 'timeline/addTimelineItem') {
    // We can't know the ID before it's generated, so we'll need to handle this differently
    // Store the payload and let the reducer generate the ID, then capture it
    // For now, return a special marker that will be resolved after the action is applied
    return {
      type: 'timeline/removeTimelineItem',
      payload: '__LAST_ITEM__', // Special marker to remove last item
      meta: { skipHistory: true },
    };
  }

  if (type === 'timeline/removeTimelineItem') {
    // Find the removed item from state (before removal)
    const removedItem = state.timeline.items.find((item) => item.id === payload);
    if (!removedItem) return null;

    // Reconstruct addTimelineItem payload
    // IMPORTANT: We need to capture the full item data, not just reference the asset
    // because the asset might be removed separately
    const asset = state.assets.videos
      .concat(state.assets.images)
      .concat(state.assets.documents)
      .concat(state.assets.questions)
      .concat(state.assets.texts)
      .concat(state.assets.audios)
      .concat(state.assets.assignedSections)
      .find((a) => a.id === removedItem.asset_id);

    // If asset doesn't exist, we can't restore - return null
    // This should be rare, but handle it gracefully
    if (!asset) {
      return null;
    }

    // Find insertion position - use the item's current order to determine where to insert
    const itemIndex = state.timeline.items.findIndex((i) => i.id === payload);
    // Find the item that should come before this one
    const prevItem = itemIndex > 0 ? state.timeline.items[itemIndex - 1] : null;

    // Store the full item data for restoration
    return {
      type: 'timeline/addTimelineItem',
      payload: {
        id: removedItem.id, // Preserve the original ID
        assetId: removedItem.asset_id,
        duration: removedItem.duration,
        trimStart: removedItem.settings?.trim_start ?? 0,
        trimEnd: removedItem.settings?.trim_end ?? 0,
        metadata: removedItem.metadata || {},
        anchorItemId: prevItem?.id || null, // Use previous item as anchor, or null for beginning
        shouldAutoSeek: false,
      },
      meta: { skipHistory: true },
    };
  }

  if (type === 'timeline/updateItemTrim') {
    // Find the item to restore previous trim values
    const item = state.timeline.items.find((i) => i.id === payload.itemId);
    if (!item) return null;

    return {
      type: 'timeline/updateItemTrim',
      payload: {
        itemId: payload.itemId,
        trimStart: item.settings.trim_start,
        trimEnd: item.settings.trim_end,
        originalDuration: payload.originalDuration,
      },
      meta: { skipHistory: true },
    };
  }

  if (type === 'timeline/reorderItems') {
    // Store current order as inverse
    return {
      type: 'timeline/reorderItems',
      payload: [...state.timeline.items],
      meta: { skipHistory: true },
    };
  }

  // Asset actions
  if (type === 'assets/addAsset') {
    return {
      type: 'assets/removeAsset',
      payload: payload.id,
      meta: { skipHistory: true },
    };
  }

  if (type === 'assets/removeAsset') {
    // Find the removed asset from all asset arrays (before removal)
    const allAssets = [
      ...state.assets.videos,
      ...state.assets.images,
      ...state.assets.documents,
      ...state.assets.questions,
      ...state.assets.texts,
      ...state.assets.audios,
      ...state.assets.assignedSections,
    ];
    const removedAsset = allAssets.find((a) => a.id === payload);
    if (!removedAsset) return null;

    return {
      type: 'assets/addAsset',
      payload: removedAsset,
      meta: { skipHistory: true },
    };
  }

  if (type === 'assets/updateAsset') {
    // Find the asset to restore previous values (before update)
    const allAssets = [
      ...state.assets.videos,
      ...state.assets.images,
      ...state.assets.documents,
      ...state.assets.questions,
      ...state.assets.texts,
      ...state.assets.audios,
      ...state.assets.assignedSections,
    ];
    const asset = allAssets.find((a) => a.id === payload.id);
    if (!asset) return null;

    // Create inverse update with previous values
    const previousChanges = {};
    Object.keys(payload.changes).forEach((key) => {
      previousChanges[key] = asset[key];
    });

    return {
      type: 'assets/updateAsset',
      payload: {
        id: payload.id,
        changes: previousChanges,
      },
      meta: { skipHistory: true },
    };
  }

  return null;
}

/**
 * Undo/Redo middleware
 */
export const undoRedoMiddleware = (store) => (next) => (action) => {
  const state = store.getState();

  // Skip history tracking for actions marked with skipHistory (undo/redo operations)
  if (action.meta?.skipHistory) {
    return next(action);
  }

  // Handle undo/redo actions
  if (action.type === 'undoRedo/undo') {
    if (state.undoRedo.past.length === 0) {
      return next(action); // Nothing to undo
    }

    // Get the last action pair BEFORE executing undo
    const lastPair = state.undoRedo.past[state.undoRedo.past.length - 1];

    // Check if this is a transaction (multiple actions) or single action
    const isTransaction = lastPair?.originalActions && lastPair?.inverseActions;

    if (isTransaction) {
      if (!lastPair?.inverseActions || lastPair.inverseActions.length === 0) {
        return next(action);
      }
    } else if (!lastPair?.inverseAction) {
      return next(action);
    }

    // Execute undo reducer FIRST (removes from past, adds to future)
    // This updates the history state
    next(action);

    // Dispatch inverse actions (in reverse order for transactions)
    if (isTransaction) {
      // For transactions, dispatch inverse actions in reverse order
      const inverseActions = [...lastPair.inverseActions].reverse();
      
      inverseActions.forEach((inverseAction) => {
        const actionToDispatch = {
          type: inverseAction.type,
          payload: inverseAction.payload,
          meta: { skipHistory: true },
        };
        
        // Handle ID resolution for removeTimelineItem
        if (actionToDispatch.type === 'timeline/removeTimelineItem') {
          const currentState = store.getState();
          const itemExists = currentState.timeline.items.some(item => item.id === actionToDispatch.payload);
          
          if (!itemExists) {
            // Try to find by assetId from the corresponding original action
            const originalActionIndex = lastPair.originalActions.findIndex(
              a => a.type === 'timeline/addTimelineItem' && a.payload?.assetId
            );
            if (originalActionIndex >= 0) {
              const originalAction = lastPair.originalActions[originalActionIndex];
              const itemsWithAsset = currentState.timeline.items.filter(
                item => item.asset_id === originalAction.payload.assetId
              );
              if (itemsWithAsset.length > 0) {
                const itemToRemove = itemsWithAsset[itemsWithAsset.length - 1];
                actionToDispatch.payload = itemToRemove.id;
              }
            }
          }
        }
        
        store.dispatch(actionToDispatch);
      });
    } else {
      // Single action
      let inverseAction = {
        type: lastPair.inverseAction.type,
        payload: lastPair.inverseAction.payload,
        meta: { skipHistory: true },
      };
      
      // If inverse is removeTimelineItem, verify the item exists with that ID
      // If not, try to find it by assetId
      if (inverseAction.type === 'timeline/removeTimelineItem') {
        const currentState = store.getState();
        const itemExists = currentState.timeline.items.some(item => item.id === inverseAction.payload);
        
        if (!itemExists) {
          // Item with stored ID doesn't exist, try to find by matching the original action's assetId
          const { originalAction } = lastPair;
          if (originalAction?.type === 'timeline/addTimelineItem' && originalAction?.payload?.assetId) {
            // Find the most recent item with this assetId
            const itemsWithAsset = currentState.timeline.items.filter(
              item => item.asset_id === originalAction.payload.assetId
            );
            if (itemsWithAsset.length > 0) {
              // Use the last one (most recently added)
              const itemToRemove = itemsWithAsset[itemsWithAsset.length - 1];
              inverseAction = {
                ...inverseAction,
                payload: itemToRemove.id,
              };
            } else {
              return lastPair; // Can't undo, item doesn't exist
            }
          }
        }
      }
      
      store.dispatch(inverseAction);
    }

    syncDirtyState(store);
    return lastPair;
  }

  if (action.type === 'undoRedo/redo') {
    if (state.undoRedo.future.length === 0) {
      return next(action); // Nothing to redo
    }

    // Get the last action pair BEFORE executing redo
    const lastPair = state.undoRedo.future[state.undoRedo.future.length - 1];

    // Check if this is a transaction (multiple actions) or single action
    const isTransaction = lastPair?.originalActions && lastPair?.inverseActions;

    if (isTransaction) {
      if (!lastPair?.originalActions || lastPair.originalActions.length === 0) {
        return next(action);
      }
    } else if (!lastPair?.originalAction) {
      return next(action);
    }

    // Execute redo reducer FIRST (removes from future, adds to past)
    // This updates the history state
    next(action);

    // Dispatch original actions to re-apply the change
    if (isTransaction) {
      // For transactions, dispatch original actions in order
      lastPair.originalActions.forEach((originalAction) => {
        const actionToDispatch = {
          ...originalAction,
          meta: { skipHistory: true },
        };
        store.dispatch(actionToDispatch);
      });
      
      // Update IDs if needed (for addTimelineItem in transactions)
      const addTimelineItemIndex = lastPair.originalActions.findIndex(
        a => a.type === 'timeline/addTimelineItem' && !a.payload.id
      );
      if (addTimelineItemIndex >= 0) {
        const stateAfterRedo = store.getState();
        const newItemId = stateAfterRedo.timeline.lastAddedItemId;
        const correspondingInverseIndex = lastPair.inverseActions.length - 1 - addTimelineItemIndex;
        if (newItemId && lastPair.inverseActions[correspondingInverseIndex]?.payload !== newItemId) {
          const updatedInverseActions = [...lastPair.inverseActions];
          updatedInverseActions[correspondingInverseIndex] = {
            ...updatedInverseActions[correspondingInverseIndex],
            payload: newItemId,
          };
          store.dispatch(updateLastInverseAction({ inverseActions: updatedInverseActions }));
        }
      }
    } else {
      // Single action
      const originalAction = {
        ...lastPair.originalAction,
        meta: { skipHistory: true },
      };
      
      store.dispatch(originalAction);
      
      // For addTimelineItem, if ID was generated during redo, update the stored inverse action
      if (originalAction.type === 'timeline/addTimelineItem' && !originalAction.payload.id) {
        const stateAfterRedo = store.getState();
        const newItemId = stateAfterRedo.timeline.lastAddedItemId;
        if (newItemId && newItemId !== lastPair.inverseAction.payload) {
          const updatedInverseAction = {
            type: lastPair.inverseAction.type,
            payload: newItemId,
          };
          
          store.dispatch(updateLastInverseAction({ inverseAction: updatedInverseAction }));
        }
      }
    }

    syncDirtyState(store);
    return lastPair;
  }

  // Check if action is undoable (has meta.undoable flag)
  if (action.meta?.undoable) {
    // IMPORTANT: If there's a future stack, clear it when new action is performed
    // This means user did something new after undo, so we can't redo anymore
    if (state.undoRedo.future.length > 0) {
      // Clear future by dispatching clearHistory, but we'll rebuild past
      // Actually, we should just clear future in addToHistory, which we already do
    }

    // Create inverse action BEFORE applying the current action
    // Capture current state snapshot for inverse action creation
    let inverseAction = createInverseAction(action, state);

    // Apply the current action
    const result = next(action);

    // For addTimelineItem, we need to get the actual ID after it's been added
    if (action.type === 'timeline/addTimelineItem' && inverseAction?.payload === '__LAST_ITEM__') {
      const newState = store.getState();
      // Try to find the item by lastAddedItemId first (most reliable)
      const addedItemId = newState.timeline.lastAddedItemId;
      if (addedItemId) {
        inverseAction = {
          type: 'timeline/removeTimelineItem',
          payload: addedItemId,
          meta: { skipHistory: true },
        };
      } else {
        // Fallback: find by assetId - get the most recently added one
        const itemsWithAsset = newState.timeline.items.filter(
          (i) => i.asset_id === action.payload.assetId
        );
        // Get the last one (most recently added)
        const item = itemsWithAsset[itemsWithAsset.length - 1];
        if (item) {
          inverseAction = {
            type: 'timeline/removeTimelineItem',
            payload: item.id,
            meta: { skipHistory: true },
          };
        } else {
          inverseAction = null; // Couldn't find the item, skip
        }
      }
    }
    
    // IMPORTANT: For addTimelineItem, ensure we store the actual generated ID in the original action
    // so that when we redo, we can use the same ID
    let actionToStore = action;
    if (action.type === 'timeline/addTimelineItem' && !action.payload.id) {
      // ID will be generated by reducer, we need to capture it and update the stored action
      const newState = store.getState();
      const addedItemId = newState.timeline.lastAddedItemId;
      if (addedItemId) {
        // Create a new action object with the generated ID
        // This ensures redo will use the same ID
        actionToStore = {
          ...action,
          payload: { ...action.payload, id: addedItemId },
        };
      }
    }

    // Add both original and inverse actions to history
    // This will automatically clear future if it exists
    if (inverseAction) {
      // Check if we should group this with the previous action (transaction grouping)
      // If last action was addAsset and current is addTimelineItem with same assetId, group them
      const currentState = store.getState();
      const lastHistoryEntry = currentState.undoRedo.past[currentState.undoRedo.past.length - 1];
      
      if (
        lastHistoryEntry?.originalAction?.type === 'assets/addAsset' &&
        action.type === 'timeline/addTimelineItem' &&
        action.payload.assetId === lastHistoryEntry.originalAction.payload.id
      ) {
        // Group them as a transaction
        const cleanOriginalActions = [
          lastHistoryEntry.originalAction,
          {
            type: actionToStore.type,
            payload: actionToStore.payload,
          },
        ];
        
        const cleanInverseActions = [
          lastHistoryEntry.inverseAction,
          {
            type: inverseAction.type,
            payload: inverseAction.payload,
          },
        ];
        
        // Replace the last entry with the transaction
        store.dispatch(replaceLastWithTransaction({ 
          originalActions: cleanOriginalActions, 
          inverseActions: cleanInverseActions 
        }));
      } else {
        // Single action, add normally
        const cleanOriginalAction = {
          type: actionToStore.type,
          payload: actionToStore.payload,
        };
        
        const cleanInverseAction = {
          type: inverseAction.type,
          payload: inverseAction.payload,
        };
        
        store.dispatch(addToHistory({ 
          originalAction: cleanOriginalAction, 
          inverseAction: cleanInverseAction 
        }));
      }
    }

    syncDirtyState(store);
    return result;
  }

  // Clear history on clear actions
  if (
    action.type === 'timeline/clearTimeline' ||
    action.type === 'assets/clearAssets' ||
    action.type === 'undoRedo/clearHistory'
  ) {
    const result = next(action);
    if (action.type !== 'undoRedo/clearHistory') {
      store.dispatch({ type: 'undoRedo/clearHistory' });
    }
    syncDirtyState(store);
    return result;
  }

  // Non-undoable action, just pass through
  return next(action);
};

